/**
 * Troubleshooting Section Component
 * Displays common errors and their solutions in an expandable format
 */

import { ExpandableSection } from "./ExpandableSection";
import {
  AlertIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  InfoIcon,
} from "./icons";

export interface TroubleshootingItem {
  error: string;
  errorDescription?: string;
  solution: string;
  command?: string;
}

interface TroubleshootingSectionProps {
  title?: string;
  items: TroubleshootingItem[];
  defaultExpanded?: boolean;
}

export function TroubleshootingSection({
  title = "Having trouble? Common issues and solutions",
  items,
  defaultExpanded = false,
}: TroubleshootingSectionProps) {
  return (
    <ExpandableSection
      title={title}
      variant="warning"
      defaultExpanded={defaultExpanded}
      icon={
        <AlertIcon className="w-4 h-4 text-amber-600 dark:text-amber-400" />
      }
    >
      <div className="space-y-3">
        {items.map((item, index) => (
          <div
            key={index}
            className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
          >
            {/* Error message */}
            <div className="flex items-start gap-2 mb-2">
              <ExclamationCircleIcon className="w-4 h-4 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <code className="text-xs font-mono bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-2 py-1 rounded block break-all">
                  {item.error}
                </code>
                {item.errorDescription && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {item.errorDescription}
                  </p>
                )}
              </div>
            </div>

            {/* Solution */}
            <div className="flex items-start gap-2 mt-3">
              <CheckCircleIcon className="w-4 h-4 text-green-500 dark:text-green-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <span className="font-medium text-green-700 dark:text-green-400">
                    Solution:{" "}
                  </span>
                  {item.solution}
                </p>
                {item.command && (
                  <code className="mt-2 text-xs font-mono bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-2 py-1 rounded block">
                    {item.command}
                  </code>
                )}
              </div>
            </div>
          </div>
        ))}

        {/* Still stuck message */}
        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex items-start gap-2">
            <InfoIcon className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-700 dark:text-blue-300">
              <strong>Still stuck?</strong> Try closing your terminal completely
              and opening a fresh one, then start from the beginning of this
              step. Sometimes a fresh start helps!
            </p>
          </div>
        </div>
      </div>
    </ExpandableSection>
  );
}
